import {
  IBudget,
  IBudgetProposals,
  IProposal,
  IStory,
  IStoryContent
} from '@opengov/stories-shared/types';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import SvgIcon from '@mui/material/SvgIcon';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Close as CloseIcon } from '@opengov/react-capital-assets';
import { useQueryClient } from '@tanstack/react-query';

import React, { useState } from 'react';

import { IModalMessages } from '../../../../types';
import { styles } from './ReplaceProposalModal.styles';
import { useGetProposalsInBudget } from '../useGetProposalsInBudget';
import { MergeTagsOperation } from '../../../../stories/common/context/MergeTagsContext';
import {
  useReplaceProposalInStory,
  useUpdateAllStoryContentsMergeTagsDB
} from '../../../../stories/components/StoryView/hooks';

export type IReplaceProposalModalProps = {
  messages: IModalMessages;
  selectedStoryId: string;
  budget: IBudget;
  replacingProposal: IBudgetProposals;
  isTemplate: boolean;
  story: IStory;
  publicationId?: string;
  userId?: number;
  storyContents: IStoryContent[];
  entityId: number;
  onClose(): void;
}

export const ReplaceProposalModal: React.FC<IReplaceProposalModalProps> = ({
  messages,
  budget,
  isTemplate,
  story,
  publicationId,
  userId,
  storyContents,
  selectedStoryId,
  replacingProposal,
  entityId,
  onClose
}) => {
  const queryClient = useQueryClient();
  const { mutateAsync: doReplaceProposal } = useReplaceProposalInStory();
  const { mutateAsync: doUpdateMergeTags } = useUpdateAllStoryContentsMergeTagsDB();

  const [selectedProposal, setSelectedProposal] = useState<IProposal | null>(null);
  const [isReplacing, setIsReplacing] = useState<boolean>(false);
  const { isLoadingProposals, proposalsList } = useGetProposalsInBudget(
    budget.budgetId,
    budget.proposals
  );

  const onChangeProposalSelection = (proposal: IProposal) => {
    proposal ? setSelectedProposal(proposal) : setSelectedProposal(null);
  };

  const replaceProposal = async (): Promise<void> => {
    setIsReplacing(true);
    try {
      const { mergeTags } = await doReplaceProposal({
        id: replacingProposal.id,
        proposalId: selectedProposal!.id,
        storyId: selectedStoryId,
        entityId
      });
      const proposalsReplacementMap = {
        [replacingProposal.proposalId]: { proposalId: selectedProposal!.id }
      };
      await doUpdateMergeTags({
        isTemplate,
        story,
        storyContents,
        publicationId: publicationId ?? '',
        userId: userId ?? 0,
        storyMergeTagsOperation: {
          operation: MergeTagsOperation.Replace,
          mergeTags,
          proposalsReplacementMap
        }
      });
      void queryClient.invalidateQueries({ queryKey: ['storyView', 'proposals'] });
    } finally {
      setIsReplacing(false);
      onClose();
    }
  };

  const renderProposalDropdown = (): JSX.Element => {
    return (
      <Box sx={styles.dropdownContainer}>
        <Typography id="replace-proposal-label" variant="body2">{messages.selectNewProposal}</Typography>
        <Autocomplete
          options={proposalsList ?? []}
          getOptionLabel={(option) => option.title}
          value={selectedProposal}
          onChange={(_event, value) => onChangeProposalSelection(value)}
          size="small"
          disablePortal
          loading={isLoadingProposals}
          renderOption={(props, option) => <li {...props}>{option.title}</li>}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={messages.enterProposals}
              inputProps={{ ...params.inputProps, 'aria-labelledby': 'replace-proposal-label', className: 'no-format' }}
            />
          )}
        />
      </Box>
    );
  };

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: styles.dialogPaper }} aria-labelledby="replace-proposal-dialog-title">
      <DialogTitle id="replace-proposal-dialog-title" sx={styles.dialogTitle}>
        {messages.replaceProposal}
        <IconButton aria-label="close" onClick={onClose} size="small" sx={styles.closeButton}>
          <SvgIcon component={CloseIcon} inheritViewBox />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        <Box sx={styles.content}>
          <WarningAmberIcon sx={styles.warningIcon} aria-hidden="true" />
          <Box sx={styles.headerContent}>
            <Typography variant="body2">{messages.replaceProposalDesc}</Typography>
            {replacingProposal && renderProposalDropdown()}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          data-test="cancel-button"
          onClick={onClose}
        >
          {messages.cancel}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={!selectedProposal}
          data-test="replace-button"
          onClick={replaceProposal}
          startIcon={isReplacing ? <CircularProgress size={16} color="inherit" aria-label="Loading" /> : undefined}
        >
          {messages.replace}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
